/**
 * Post-login account setup — name, phone, picture, birthday, passkey.
 *
 * Route: /auth/setup
 * Shown on first login (any method) when the user has no display name set.
 */

import { LitElement, html, css, nothing } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { authService } from '../../services/auth/auth-service.js'
import { updateUserAttributes } from '../../services/auth/cognito-api.js'
import { registerPasskey } from '../../services/auth/credential-service.js'
import { logger } from '../../utils/logger.js'
import { resizeImageToDataUrl } from '../../utils/image-resize.js'

import '@material/web/button/filled-button.js'
import '@material/web/button/text-button.js'
import '@material/web/textfield/outlined-text-field.js'
import '@material/web/checkbox/checkbox.js'
import '@material/web/icon/icon.js'
import '@material/web/progress/circular-progress.js'

const log = logger.create('auth-setup')

@customElement('auth-account-setup')
export class AuthAccountSetup extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
      padding: var(--cairn-spacing-xl, 32px) var(--cairn-spacing-md, 16px);
      box-sizing: border-box;
    }

    .container {
      max-width: 400px;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    h2 {
      font-size: var(--cairn-text-size-xl, 24px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0 0 var(--cairn-spacing-xs, 4px);
    }

    .subtitle {
      font-size: var(--cairn-text-size-md, 16px);
      color: var(--cairn-text-secondary-color, #64748b);
      margin: 0 0 var(--cairn-spacing-xl, 32px);
      text-align: center;
      line-height: var(--cairn-line-height, 1.5);
    }

    .form {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: var(--cairn-spacing-md, 16px);
    }

    .name-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: var(--cairn-spacing-md, 16px);
    }

    md-outlined-text-field {
      width: 100%;
      --md-outlined-text-field-container-shape: var(--cairn-border-radius, 8px);
    }

    .avatar-section {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-bottom: var(--cairn-spacing-lg, 24px);
    }

    .avatar {
      width: 96px;
      height: 96px;
      border-radius: 50%;
      background: var(--cairn-primary-color, #3b82f6);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 36px;
      font-weight: 500;
      position: relative;
      overflow: hidden;
      cursor: pointer;
    }

    .avatar img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .avatar-overlay {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.4);
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.2s;
    }

    .avatar:hover .avatar-overlay {
      opacity: 1;
    }

    .avatar-overlay md-icon {
      color: white;
      --md-icon-size: 28px;
    }

    .avatar input[type='file'] {
      display: none;
    }

    .avatar-hint {
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
      margin-top: var(--cairn-spacing-xs, 4px);
    }

    .passkey-option {
      display: flex;
      align-items: flex-start;
      gap: var(--cairn-spacing-sm, 8px);
      padding: var(--cairn-spacing-md, 16px);
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius-lg, 12px);
    }

    .passkey-option md-checkbox {
      margin-top: 2px;
    }

    .passkey-info {
      flex: 1;
    }

    .passkey-title {
      font-size: var(--cairn-text-size-md, 16px);
      font-weight: var(--cairn-font-weight-medium, 500);
      color: var(--cairn-text-color, #1e293b);
      margin: 0 0 var(--cairn-spacing-xs, 4px);
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-xs, 4px);
    }

    .passkey-title md-icon {
      font-size: 20px;
      --md-icon-size: 20px;
      color: var(--cairn-primary-color, #3b82f6);
    }

    .passkey-description {
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
      line-height: var(--cairn-line-height, 1.5);
      margin: 0;
    }

    .actions {
      display: flex;
      flex-direction: column;
      gap: var(--cairn-spacing-sm, 8px);
      width: 100%;
      margin-top: var(--cairn-spacing-lg, 24px);
    }

    md-filled-button {
      width: 100%;
      --md-filled-button-container-height: 48px;
      --md-filled-button-container-shape: var(--cairn-border-radius, 8px);
    }

    .error-message {
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-error-color, #dc2626);
      margin: 0;
      text-align: center;
    }

    .loading-overlay {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--cairn-spacing-md, 16px);
      padding: var(--cairn-spacing-xl, 32px);
      color: var(--cairn-text-secondary-color, #64748b);
    }
  `

  @state() private firstName = ''
  @state() private lastName = ''
  @state() private displayName = ''
  @state() private phone = ''
  @state() private birthday = ''
  @state() private pictureDataUrl: string | undefined
  @state() private enablePasskey = true
  @state() private saving = false
  @state() private error = ''
  @state() private supportsPasskeys = false
  private displayNameManuallyEdited = false

  override connectedCallback(): void {
    super.connectedCallback()
    this.detectPasskeySupport()
  }

  private async detectPasskeySupport(): Promise<void> {
    try {
      if (window.PublicKeyCredential) {
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        this.supportsPasskeys = available
      }
    } catch {
      this.supportsPasskeys = false
    }
  }

  private updateDisplayNameFromNames(): void {
    if (!this.displayNameManuallyEdited) {
      this.displayName = `${this.firstName} ${this.lastName}`.trim()
    }
  }

  private handlePictureUpload(e: Event): void {
    const input = e.target as HTMLInputElement
    const file = input.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      this.error = 'Please select an image file.'
      return
    }

    void this.resizeAndSetPicture(file)
  }

  private async resizeAndSetPicture(file: File): Promise<void> {
    try {
      this.pictureDataUrl = await resizeImageToDataUrl(file)
      this.error = ''
    } catch {
      this.error = 'Image is too large even after compression. Try a simpler image.'
    }
  }

  private getInitials(): string {
    const first = this.firstName.charAt(0).toUpperCase()
    const last = this.lastName.charAt(0).toUpperCase()
    return first + last || '?'
  }

  private async handleSave(): Promise<void> {
    this.saving = true
    this.error = ''

    try {
      const token = authService.getAccessToken()
      if (!token) throw new Error('Not authenticated')

      const attributes: Record<string, string> = {}
      if (this.firstName.trim()) {
        attributes['given_name'] = this.firstName.trim()
      }
      if (this.lastName.trim()) {
        attributes['family_name'] = this.lastName.trim()
      }
      if (this.displayName.trim()) {
        attributes['name'] = this.displayName.trim()
      }
      if (this.phone.trim()) {
        // Cognito requires E.164 format (+<country><number>)
        let phone = this.phone.trim().replace(/[\s\-().]/g, '')
        if (!phone.startsWith('+')) {
          phone = '+1' + phone // Default to US country code
        }
        attributes['phone_number'] = phone
      }
      if (this.birthday) {
        attributes['birthdate'] = this.birthday
      }
      // picture attribute is URL-only (2048 char limit) — needs S3 upload

      if (Object.keys(attributes).length > 0) {
        await updateUserAttributes(token, attributes)
        // Refresh tokens so auth state picks up the new claims
        await authService.refreshSession()
      }

      // Register passkey if opted in
      if (this.enablePasskey && this.supportsPasskeys) {
        try {
          await registerPasskey()
        } catch (err) {
          // Passkey registration is optional — log but don't block
          log.warn('Passkey registration skipped:', err)
        }
      }

      const { router } = await import('../../utils/router.js')
      if (authService.needsEula()) {
        router.navigate('/auth/eula')
      } else {
        router.navigate('/')
      }
    } catch (err) {
      log.error('Setup save failed:', err)
      this.error = 'Failed to save. Please try again.'
      this.saving = false
    }
  }

  private async handleSkip(): Promise<void> {
    const { router } = await import('../../utils/router.js')
    if (authService.needsEula()) {
      router.navigate('/auth/eula')
    } else {
      router.navigate('/')
    }
  }

  override render(): unknown {
    if (this.saving) {
      return html`
        <div class="container">
          <div class="loading-overlay">
            <md-circular-progress indeterminate></md-circular-progress>
            <span>Saving...</span>
          </div>
        </div>
      `
    }

    return html`
      <div class="container">
        <h2>Set up your profile</h2>
        <p class="subtitle">
          Add some details to personalize your experience. You can always change these later.
        </p>

        <div class="avatar-section">
          <label class="avatar">
            ${this.pictureDataUrl
              ? html`<img src="${this.pictureDataUrl}" alt="Profile picture" />`
              : html`<span>${this.getInitials()}</span>`}
            <div class="avatar-overlay">
              <md-icon>add_a_photo</md-icon>
            </div>
            <input type="file" accept="image/*" @change=${this.handlePictureUpload} />
          </label>
          <span class="avatar-hint">Tap to add a photo</span>
        </div>

        ${this.error ? html`<p class="error-message">${this.error}</p>` : nothing}

        <div class="form">
          <div class="name-row">
            <md-outlined-text-field
              label="First name"
              .value=${this.firstName}
              @input=${(e: Event): void => {
                this.firstName = (e.target as HTMLInputElement).value
                this.updateDisplayNameFromNames()
              }}
            ></md-outlined-text-field>

            <md-outlined-text-field
              label="Last name"
              .value=${this.lastName}
              @input=${(e: Event): void => {
                this.lastName = (e.target as HTMLInputElement).value
                this.updateDisplayNameFromNames()
              }}
            ></md-outlined-text-field>
          </div>

          <md-outlined-text-field
            label="Display name"
            .value=${this.displayName}
            @input=${(e: Event): void => {
              this.displayName = (e.target as HTMLInputElement).value
              this.displayNameManuallyEdited = true
            }}
            supporting-text="How others will see you"
          ></md-outlined-text-field>

          <md-outlined-text-field
            label="Phone number"
            type="tel"
            placeholder="+1 555 123 4567"
            .value=${this.phone}
            @input=${(e: Event): void => {
              this.phone = (e.target as HTMLInputElement).value
            }}
            supporting-text="Optional — include country code (e.g. +1)"
          ></md-outlined-text-field>

          <md-outlined-text-field
            label="Birthday"
            type="date"
            .value=${this.birthday}
            @input=${(e: Event): void => {
              this.birthday = (e.target as HTMLInputElement).value
            }}
            supporting-text="Optional"
          ></md-outlined-text-field>

          ${this.supportsPasskeys
            ? html`
                <label class="passkey-option">
                  <md-checkbox
                    ?checked=${this.enablePasskey}
                    @change=${(e: Event): void => {
                      this.enablePasskey = (e.target as HTMLInputElement).checked
                    }}
                  ></md-checkbox>
                  <div class="passkey-info">
                    <p class="passkey-title">
                      <md-icon>fingerprint</md-icon>
                      Enable fingerprint login
                    </p>
                    <p class="passkey-description">
                      Use your device's biometrics for fast, secure sign-in next time.
                    </p>
                  </div>
                </label>
              `
            : nothing}
        </div>

        <div class="actions">
          <md-filled-button @click=${this.handleSave}>Save</md-filled-button>
          <md-text-button @click=${this.handleSkip}>Skip for now</md-text-button>
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'auth-account-setup': AuthAccountSetup
  }
}
